/**
 * The main colours in a picture (a masjid's logo, or any image): up to `max`, the most used first. Used by the website
 * and the iPhone app (in its engine).
 *
 * How it finds them, so a logo on a plain background gives every one of its colours (as Canva's colour picker does):
 * - The background (the colour around the picture's edges) is set aside, so it doesn't swamp the logo's own colours.
 *   A coloured background (cream, say) is still offered, after the logo's colours; plain white isn't.
 * - Pixels on the edge between two shapes (the blend that smoothing makes) are skipped: they're mixes, not colours.
 * - Small shapes still count: what's too small to be a colour is judged against the logo, not the whole picture.
 * - Shades that look alike to the eye are one colour; ones that look different stay apart.
 */
export async function coloursFromImage(src: string | Blob, max = 6): Promise<string[]> {
  const url = typeof src === 'string' ? src : URL.createObjectURL(src);
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    await img.decode();
    // Big enough that thin bars and small dots keep solid pixels in the middle, small enough to be instant.
    const side = 220, s = Math.min(1, side / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
    const w = Math.max(1, Math.round((img.naturalWidth || side) * s)), h = Math.max(1, Math.round((img.naturalHeight || side) * s));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return [];
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);
    return paletteOf(ctx.getImageData(0, 0, w, h).data, max, w);
  } catch {
    return [];
  } finally {
    if (typeof src !== 'string') URL.revokeObjectURL(url);
  }
}

type RGB = [number, number, number];
type Lab = [number, number, number];

/**
 * The palette of raw RGBA pixels (exported for tests). With the picture's `width`, the background and the edges
 * between shapes are recognised; without it (a plain list of pixels) every opaque pixel counts and only white is
 * left out.
 */
export function paletteOf(px: ArrayLike<number>, max = 6, width = 0): string[] {
  const n = Math.floor(px.length / 4);
  const height = width > 0 ? Math.floor(n / width) : 0;
  const grid = width > 0 && height > 2 && width > 2;
  const at = (i: number): RGB => [px[i * 4]!, px[i * 4 + 1]!, px[i * 4 + 2]!];
  const opaque = (i: number) => px[i * 4 + 3]! >= 128;

  // 1. The background: the colour that fills most of the picture's border (if one does).
  let bg: RGB | null = null;
  if (grid) {
    const border: number[] = [];
    for (let x = 0; x < width; x++) border.push(x, (height - 1) * width + x);
    for (let y = 1; y < height - 1; y++) border.push(y * width, y * width + width - 1);
    const seen = border.filter(opaque);
    if (seen.length >= border.length * 0.5) {
      const groups = cluster(seen.map((i) => ({ c: at(i), n: 1 })), 10);
      const top = groups[0];
      if (top && top.n >= seen.length * 0.6) bg = top.c;
    }
  } else {
    bg = [255, 255, 255];
  }
  const bgLab = bg ? lab(bg) : null;

  // 2. Every solid pixel: opaque, not the background, and not on an edge where two colours blend.
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  const near = (i: number, j: number) => {
    if (!opaque(j)) return false;
    const a = i * 4, b = j * 4;
    return Math.abs(px[a]! - px[b]!) + Math.abs(px[a + 1]! - px[b + 1]!) + Math.abs(px[a + 2]! - px[b + 2]!) <= 36;
  };
  for (let i = 0; i < n; i++) {
    if (!opaque(i)) continue;
    const c = at(i);
    if (bgLab && deltaE(lab(c), bgLab) < 9) continue;
    if (!bg && c[0] > 242 && c[1] > 242 && c[2] > 242) continue;
    if (grid) {
      const x = i % width, y = (i - x) / width;
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) continue;
      if (!near(i, i - 1) || !near(i, i + 1) || !near(i, i - width) || !near(i, i + width)) continue;
    }
    // Fine buckets (4 bits a channel); similar ones are joined below by how alike they look.
    const key = ((c[0] >> 4) << 8) | ((c[1] >> 4) << 4) | (c[2] >> 4);
    const k = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    k.n++; k.r += c[0]; k.g += c[1]; k.b += c[2];
    buckets.set(key, k);
  }

  // 3. Shades that look alike become one colour; a colour needs at least 0.5% of the logo (not of the whole picture).
  const solid = [...buckets.values()].reduce((s, k) => s + k.n, 0);
  const groups = cluster([...buckets.values()].map((k) => ({ c: [k.r / k.n, k.g / k.n, k.b / k.n] as RGB, n: k.n })), 11)
    .filter((g) => g.n >= Math.max(2, solid * 0.005));

  const out = groups.slice(0, max).map((g) => g.c);
  // A coloured background (not plain white or black) is one of the brand's colours too: offered last.
  if (bg && grid && out.length < max) {
    const l = lab(bg)[0];
    if (l < 96 && l > 6 && !out.some((c) => deltaE(lab(c), bgLab!) < 11)) out.push(bg);
  }
  return out.map(hex);
}

/** Joins colours that look alike (within `limit` in CIE Lab), most used first; each group keeps its main shade. */
function cluster(items: Array<{ c: RGB; n: number }>, limit: number): Array<{ c: RGB; n: number }> {
  const groups: Array<{ c: RGB; n: number; l: Lab; sum: RGB }> = [];
  for (const it of [...items].sort((a, b) => b.n - a.n)) {
    const l = lab(it.c);
    let best: (typeof groups)[number] | null = null, bestD = limit;
    for (const g of groups) { const d = deltaE(l, g.l); if (d < bestD) { bestD = d; best = g; } }
    if (best) {
      best.n += it.n;
      best.sum = [best.sum[0] + it.c[0] * it.n, best.sum[1] + it.c[1] * it.n, best.sum[2] + it.c[2] * it.n];
    } else {
      groups.push({ c: it.c, n: it.n, l, sum: [it.c[0] * it.n, it.c[1] * it.n, it.c[2] * it.n] });
    }
  }
  // Each colour is the average of the shades joined into it (so JPEG speckle evens out), most used first.
  return groups.map((g) => ({ c: [g.sum[0] / g.n, g.sum[1] / g.n, g.sum[2] / g.n] as RGB, n: g.n })).sort((a, b) => b.n - a.n);
}

/** sRGB to CIE Lab (D65), where distance matches how different two colours look. */
function lab([r, g, b]: RGB): Lab {
  const lin = (v: number) => { const x = v / 255; return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; };
  const R = lin(r), G = lin(g), B = lin(b);
  const X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047, Y = R * 0.2126 + G * 0.7152 + B * 0.0722, Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(X), fy = f(Y), fz = f(Z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

const deltaE = (a: Lab, b: Lab) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
const hex = (c: RGB) => `#${c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
